
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, TrendingUp } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { sanitizeInput } from "@/utils/security";

interface AccountOverviewProps {
  walletAddress: string;
  balance: string;
}

export const AccountOverview = ({ walletAddress, balance }: AccountOverviewProps) => {
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  const copyAddress = () => {
    // Sanitize input before copying to clipboard
    const sanitizedAddress = sanitizeInput(walletAddress);
    navigator.clipboard.writeText(sanitizedAddress);
    setIsCopied(true);
    
    toast({
      title: "Address Copied",
      description: "Wallet address copied to clipboard.",
      variant: "success"
    });
    
    // Reset copy status after 2 seconds
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Card className="md:col-span-2 border-blue-100/60 shadow-md hover:shadow-lg transition-all">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-t-lg">
        <CardTitle className="text-blue-700">Account Overview</CardTitle>
        <CardDescription>Manage your funds and view your balance</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="space-y-2">
            <div className="text-sm text-gray-500">Wallet Address</div>
            <div className="flex items-center gap-2">
              <code className="bg-gray-100 px-3 py-2 rounded-md font-mono text-sm">{walletAddress}</code>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={copyAddress} 
                className={cn("transition-all", isCopied ? "text-emerald-600" : "text-gray-500")}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2 text-right">
            <div className="text-sm text-gray-500">Available Balance</div>
            <div className="flex items-center justify-end gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <div className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-blue-500 bg-clip-text text-transparent">
                {balance} ETH
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Add the missing cn import
import { cn } from "@/lib/utils";
